"use client";

/**
 * Global modal coordinator (PANDA-1158).
 *
 * Panda's modals each open themselves from independent triggers — the
 * email-verification gate fires off a 403 fetch interceptor, the
 * exit-intent survey off a mouse-leave, the upgrade/credit walls off
 * credit guards, and so on. None of them know about the others, so when
 * a build fails or stalls several conditions can become true at once and
 * the modals stack on top of each other (gate + "Before you go..." +
 * credit wall), which is the chaos this ticket reports.
 *
 * This is a tiny shared registry every key modal reports its open/closed
 * state into. A modal can then ask "is anything else already open?"
 * before showing itself, so lower-priority modals stand down while a
 * blocking one is up and the exit-intent never pops over another modal.
 *
 * It's intentionally framework-light (a plain Set + listeners) so it
 * works across our mixed modal patterns — Radix dialogs, root-level
 * portals, hook-driven warnings — without a shared React provider.
 */

import { useSyncExternalStore } from "react";

const openModals = new Set<string>();
const listeners = new Set<() => void>();

// Timestamp (ms) of the most recent close of any registered modal (PANDA-1269).
// The cursor moving up to a modal's X — or onward to the tab strip / address
// bar right after dismissing — fires the very same top-edge `mouseout` the
// exit-intent surveys listen for. That turned a single dismissal into a
// back-to-back chain (build-quality → "Before you go…" → "What's stopping
// you publishing?"). Recording when a modal last closed lets every prompt
// distinguish "the user just closed a modal" from "the user is leaving the
// project", and stand down for a short settle window in the former case.
let lastModalClosedAt = 0;

/**
 * Settle window after any modal closes during which passive feedback /
 * retention prompts must stand down (PANDA-1269). Long enough that the
 * dismissal gesture and the immediate follow-on cursor move toward browser
 * chrome can't chain into a second prompt; short enough that a genuine exit a
 * little later still surfaces one.
 */
export const MODAL_CLOSE_COOLDOWN_MS = 8_000;

function emit() {
  for (const listener of listeners) listener();
}

/** Report a modal's open/closed state by a stable id. */
export function setModalOpen(id: string, open: boolean): void {
  const had = openModals.has(id);
  if (open && !had) {
    openModals.add(id);
    emit();
  } else if (!open && had) {
    openModals.delete(id);
    lastModalClosedAt = Date.now();
    emit();
  }
}

/**
 * True when a registered modal closed within the last `ms` (default: the
 * shared settle window). A just-closed modal means a top-edge `mouseout` is
 * almost certainly the tail of a dismissal, not exit intent — so prompts use
 * this to avoid replacing a dismissed modal with another one (PANDA-1269).
 */
export function recentlyClosedModal(
  ms: number = MODAL_CLOSE_COOLDOWN_MS,
): boolean {
  if (lastModalClosedAt === 0) return false;
  return Date.now() - lastModalClosedAt < ms;
}

/** True when a modal with this id is currently open. */
export function isModalOpen(id: string): boolean {
  return openModals.has(id);
}

/** True when any modal other than `exceptId` is currently open. */
export function isAnyModalOpen(exceptId?: string): boolean {
  for (const id of openModals) {
    if (id !== exceptId) return true;
  }
  return false;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Stable primitive snapshot so useSyncExternalStore never loops: a sorted
// comma-joined id list changes only when the open set actually changes.
function snapshot(): string {
  if (openModals.size === 0) return "";
  return Array.from(openModals).sort().join(",");
}

function serverSnapshot(): string {
  return "";
}

/** Hook: re-renders when the set of open modals changes. */
export function useOpenModals(): string[] {
  const snap = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  return snap === "" ? [] : snap.split(",");
}

/** Hook: is the modal with `id` currently open? */
export function useIsModalOpen(id: string): boolean {
  return useOpenModals().includes(id);
}

/** Hook: is any modal other than `exceptId` currently open? */
export function useAnyModalOpen(exceptId?: string): boolean {
  return useOpenModals().some((id) => id !== exceptId);
}

/**
 * Every mount of the Convert-to-Pro modal. Declared after MODAL_IDS below via
 * a lazy getter so the three ids stay in one list — a fourth mount adds itself
 * here and every stand-down guard picks it up for free.
 */
function trialConvertIds(): string[] {
  return [
    MODAL_IDS.trialConvert,
    MODAL_IDS.trialConvertGate,
    MODAL_IDS.trialConvertBilling,
  ];
}

/**
 * True when ANY Convert-to-Pro mount is open.
 *
 * Always ask through this rather than testing a single id: which of the three
 * mounts is showing depends on how the user got there, and a guard that checks
 * only one of them silently stops working the moment they arrive another way.
 */
export function isTrialConvertOpen(): boolean {
  return trialConvertIds().some((id) => openModals.has(id));
}

/** Hook form of isTrialConvertOpen — re-renders when the open set changes. */
export function useIsTrialConvertOpen(): boolean {
  const open = useOpenModals();
  return trialConvertIds().some((id) => open.includes(id));
}

/** Stable modal ids used across the app. */
export const MODAL_IDS = {
  emailGate: "email-gate",
  exitIntent: "pre-publish-exit-intent",
  upgrade: "upgrade-modal",
  creditExhausted: "credit-exhausted-modal",
  lowCredits: "low-credits-modal",
  domain: "domain-modal",
  appLive: "app-live-modal",
  // Free-app inactivation heads-up — the first step of the post-publish chain,
  // ahead of domain/appLive. Registered so the passive prompts stand down while
  // it's up and can't fire off the gesture that dismisses it.
  appInactivationNotice: "app-inactivation-notice-modal",
  publish: "publish-dialog",
  // Credit top-up (Add credits) and card-entry modals. Both are blocking
  // payment overlays the user opens deliberately, so the exit-intent survey
  // must stand down while either is up instead of popping over a purchase
  // (PANDA-1162: the top-up modal was never registered, so moving the cursor
  // toward the tab strip while it was open still surfaced the survey).
  topup: "topup-modal",
  card: "card-modal",
  // Rated credits & plans modal — the unified out-of-credits paywall shown in
  // place of upgrade/creditExhausted/topup when NEXT_PUBLIC_RATED_BILLING_ENABLED
  // is on. Same paywall class/priority as the modals it replaces.
  ratedCredits: "rated-credits-modal",
  // $1 trial offer (PANDA-1500) — the T2 dismiss-fallback shown only after a
  // FREE, trial-eligible user dismisses the full-price paywall. Registered here
  // so the paywall can never stack back on top of it: once the $1 offer is up it
  // is the single active monetization surface (QA — never show the classic
  // upgrade modal over the $1 offer). Ranks above the paywalls below.
  trialOffer: "trial-offer-modal",
  // Convert-to-Pro, for a user already INSIDE the $1 trial — the mirror of
  // trialOffer, which sells the trial to a free user. Every purchase route is
  // 403'd mid-trial (lib/trial/purchase-guard.ts), so converting is the only
  // money surface that works for this population; it therefore ranks with
  // trialOffer, above the paywalls it replaces.
  //
  // THREE ids for ONE modal, deliberately. The same TrialExhaustedModal is
  // mounted in three places (the app-wide host, the project page's credit gate,
  // and Settings → Billing) and each owns its own open state. Sharing one id
  // made them last-writer-wins over a plain Set: the project page re-asserts
  // its flag from an effect keyed on eight unrelated modal states, so any of
  // those toggling wrote `false` and silently DE-registered a convert modal the
  // host still had on screen — re-arming the very stand-down guards this id
  // exists to drive. One id per owner, and ask about them together via
  // isTrialConvertOpen / useIsTrialConvertOpen below.
  trialConvert: "trial-convert-modal",
  /** The project page's credit-gate mount (trial_exhausted). */
  trialConvertGate: "trial-convert-modal-gate",
  /** Settings → Billing's "Convert to Pro now" mount. */
  trialConvertBilling: "trial-convert-modal-billing",
  // Build-quality survey (PANDA-1244) — "Did Panda build what you expected?",
  // shown a few seconds after the first build renders. It registers here so the
  // passive exit/retention prompts stand down while it's up and can't fire off
  // the gesture that dismisses it (PANDA-1269). Outranks those prompts but sits
  // below any user-initiated flow or blocking gate.
  buildQuality: "build-quality-survey",
  // "Never built" survey (PANDA-1245) — shown on the home/empty state to users
  // who signed up but never started a build. Lowest signal, like the
  // pre-publish exit survey, so it must never stack over anything else.
  neverBuilt: "never-built-survey",
  // Never-published lifecycle survey (PANDA-1246). Lowest signal, like the
  // pre-publish exit survey — it must never stack over or interrupt anything.
  neverPublished: "never-published-survey",
  // Never-upgraded lifecycle survey (PANDA-1247). Same lowest-signal class — its
  // modal surfaces only after the upgrade modal has been dismissed (so it never
  // stacks ON the paywall), or on a later session for a published-but-free user.
  neverUpgraded: "never-upgraded-survey",
  // Referrals panel in the build canvas (PANDA-2378). A user-opened overlay,
  // like topup/card, and at their default priority: registered so the
  // build-quality survey and the passive exit prompts stand down while it is
  // up — and for the settle window after it closes — instead of opening hidden
  // beneath it and appearing the moment it is dismissed.
  referrals: "referrals-panel",
} as const;

/**
 * Priority ranking — higher wins (PANDA-1158).
 *
 * The first pass at this ticket gated modals only at RENDER time
 * ("hide if the email gate is open"), which left every lower modal's
 * state set behind the gate. Dismissing the top one then revealed the
 * next, turning the simultaneous pile-up into a dismiss-one-see-the-next
 * chain — the exact "close Verify email → Before you go… → credit wall"
 * sequence the ticket reports. The real fix is to decide ONE active modal
 * by priority at trigger time:
 *
 *   - The email-verification gate is the single blocking gate. A 403
 *     fires it BEFORE any credit is reserved, so when it's up the credit
 *     walls aren't merely lower priority, they're wrong (nothing was
 *     spent). It supersedes everything and the lower walls retire.
 *   - User-initiated flows (publish / domain / app-live) outrank the
 *     passive money walls so a wall never covers a flow the user opened.
 *   - The pre-publish exit survey is the lowest signal of all: it must
 *     never interrupt or stack over anything.
 */
const MODAL_PRIORITY: Record<string, number> = {
  [MODAL_IDS.emailGate]: 100,
  [MODAL_IDS.publish]: 60,
  [MODAL_IDS.domain]: 55,
  // Above domain/appLive because it opens BEFORE them in the same chain and
  // must never be covered by the step it precedes.
  [MODAL_IDS.appInactivationNotice]: 56,
  [MODAL_IDS.appLive]: 50,
  // The $1 trial offer outranks the full-price paywalls (40): once it's shown it
  // is the single active monetization surface, so the classic upgrade / rated
  // paywall stands down instead of stacking over it (PANDA-1500 / QA).
  [MODAL_IDS.trialOffer]: 45,
  // Same rank, same reason: for a trialing user the convert screen IS the
  // paywall, so the rated/credit walls must stand down rather than stack. All
  // three mounts rank identically — they are the same modal.
  [MODAL_IDS.trialConvert]: 45,
  [MODAL_IDS.trialConvertGate]: 45,
  [MODAL_IDS.trialConvertBilling]: 45,
  [MODAL_IDS.upgrade]: 40,
  [MODAL_IDS.creditExhausted]: 40,
  [MODAL_IDS.ratedCredits]: 40,
  [MODAL_IDS.lowCredits]: 20,
  // Above the passive exit/retention prompts so they never open over the
  // build-quality survey or off the gesture that closes it (PANDA-1269).
  [MODAL_IDS.buildQuality]: 15,
  // For an eligible unpublished exit the publish-blocker prompt takes priority
  // over the generic "Before you go" exit survey, and the two must never appear
  // sequentially from one exit attempt (PANDA-1291). Ranking it above
  // exitIntent makes that survey stand down whenever the publish-blocker is up.
  [MODAL_IDS.neverPublished]: 12,
  [MODAL_IDS.exitIntent]: 10,
  [MODAL_IDS.neverBuilt]: 10,
  [MODAL_IDS.neverUpgraded]: 10,
};

function priorityOf(id: string): number {
  return MODAL_PRIORITY[id] ?? 30;
}

/** True when a modal outranking `id` is currently open. */
export function hasHigherPriorityModalOpen(id: string): boolean {
  const mine = priorityOf(id);
  for (const open of openModals) {
    if (open !== id && priorityOf(open) > mine) return true;
  }
  return false;
}

/** Hook: is a modal outranking `id` currently open? */
export function useHasHigherPriorityModalOpen(id: string): boolean {
  return useOpenModals().some(
    (open) => open !== id && priorityOf(open) > priorityOf(id),
  );
}
